/**
 * Company branding & theme studio (B4 — Advanced).
 *
 * Employer-facing full-spectrum theming: colors (core + semantic + surfaces),
 * typography (family, size, scale, weight, line-height, letter-spacing),
 * shape (button/card/input radius), effects (shadow depth), and a raw
 * Custom CSS escape hatch. Includes import/export as JSON.
 *
 * Applied via CSS custom properties on <html>; every screen (recruiter,
 * public apply, embed board) picks it up through `BrandingContext.applyTheme`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Save, Palette, Type, Image as ImageIcon, RefreshCw, Layers, Ruler, Wand2,
    Download, Upload, Sparkles, FolderTree, RotateCcw,
} from "lucide-react";

const FONT_OPTIONS = [
    "Inter, system-ui, sans-serif",
    "Roboto, sans-serif",
    "Open Sans, sans-serif",
    "Lato, sans-serif",
    "Poppins, sans-serif",
    "Montserrat, sans-serif",
    "Nunito, sans-serif",
    "Source Sans Pro, sans-serif",
    "Work Sans, sans-serif",
    "IBM Plex Sans, sans-serif",
    "DM Sans, sans-serif",
    "Manrope, sans-serif",
    "Space Grotesk, sans-serif",
    "Playfair Display, serif",
    "Merriweather, serif",
    "Lora, serif",
    "Georgia, serif",
    "ui-monospace, SFMono-Regular, monospace",
    "JetBrains Mono, monospace",
    "Fira Code, monospace",
];

const TABS = [
    { id: "identity", label: "Identity", icon: ImageIcon },
    { id: "colors", label: "Colors", icon: Palette },
    { id: "surfaces", label: "Surfaces", icon: Layers },
    { id: "typography", label: "Typography", icon: Type },
    { id: "shape", label: "Shape & effects", icon: Ruler },
    { id: "sections", label: "Sections", icon: FolderTree },
    { id: "advanced", label: "Advanced", icon: Wand2 },
];

const SECTION_DEFS = [
    { id: "dashboard", label: "Recruiter dashboard", hint: "Overrides shown to logged-in recruiters/employers on the main dashboard." },
    { id: "candidate_profile", label: "Candidate profile", hint: "Dense candidate detail view — good place for a smaller base font size." },
    { id: "jobs_list", label: "Jobs list", hint: "Jobs table + kanban board." },
    { id: "candidates_list", label: "Candidates list", hint: "Candidates table + Excel view." },
    { id: "public_apply", label: "Public apply page", hint: "Candidate-facing job apply flow (/j/:slug)." },
    { id: "embed", label: "Embed board", hint: "External embed boards (/embed/board/:slug)." },
];

const SECTION_ALLOWED_KEYS = new Set([
    "primary_color", "accent_color", "text_color", "bg_color", "card_bg",
    "muted_color", "border_color", "link_color",
    "base_font_size", "line_height", "letter_spacing",
    "h1_scale", "h2_scale", "card_radius", "input_radius",
    "button_shape", "shadow_depth", "page_title_size", "page_title_uppercase",
]);

const RESET_THEME = {
    primary_color: "#002FA7", accent_color: "#0080FF",
    text_color: "#0f172a", bg_color: "#F9FAFB", card_bg: "#ffffff",
    muted_color: "#64748B", border_color: "#E2E8F0", link_color: "",
    success_color: "#059669", warning_color: "#D97706", danger_color: "#DC2626",
    sidebar_bg: "", header_bg: "", input_bg: "#ffffff",
    table_header_bg: "#F1F5F9", tooltip_bg: "#0f172a",
    button_text_color: "#ffffff", focus_ring_color: "",
    body_font: "Inter, system-ui, sans-serif", heading_font: "",
    display_font: "", mono_font: "",
    base_font_size: 14, h1_scale: 2.25, h2_scale: 1.75, h3_scale: 1.375,
    line_height: 1.5, letter_spacing: "normal",
    heading_weight: 800, body_weight: 400,
    button_shape: "sharp", card_radius: 0, input_radius: 0,
    shadow_depth: "none", density: "default",
    page_title_uppercase: false, page_title_size: "default", color_scheme: "light",
    custom_css: "",
};

export default function CompanyBranding() {
    const { applyTheme, refresh } = useBranding();
    const [theme, setTheme] = useState({});
    const [tab, setTab] = useState("identity");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [sectionSchema, setSectionSchema] = useState(null);
    const importRef = useRef(null);
    // Debounce timer for preview() so slider drags don't re-apply CSS vars
    // 60× per second — one final apply after a 90ms idle keeps the UI
    // responsive without repainting the whole live-preview panel per pixel.
    const previewTimer = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/company/theme");
                setTheme(data.theme || {});
            } catch (err) {
                setLoadError(formatApiError(err));
                toast.error(formatApiError(err));
            }
            finally { setLoading(false); }
        })();
        // Fetch the section schema in parallel — drives the Sections tab so
        // the field list never drifts from the backend whitelist.
        api.get("/company/theme/section-schema")
            .then((r) => setSectionSchema(r.data))
            .catch(() => setSectionSchema(null));
    }, []);

    // Any preview() call updates local theme immediately (so inputs stay
    // controlled) but debounces the expensive applyTheme() side-effect.
    const preview = (patch) => setTheme((cur) => {
        const next = { ...cur, ...patch };
        if (previewTimer.current) clearTimeout(previewTimer.current);
        previewTimer.current = setTimeout(() => applyTheme(next), 90);
        return next;
    });

    useEffect(() => () => { if (previewTimer.current) clearTimeout(previewTimer.current); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/company/theme", theme);
            applyTheme(data.theme);
            await refresh();
            toast.success("Branding saved — applied to every screen");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const doReset = () => {
        preview(RESET_THEME);
        toast.info("Reverted to defaults (not saved yet)");
    };

    const doExport = () => {
        const blob = new Blob([JSON.stringify(theme, null, 2)], { type: "application/json" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `portalwiz-theme-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
    };

    const doImport = async (file) => {
        try {
            const txt = await file.text();
            const parsed = JSON.parse(txt);
            if (typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Not a theme object");
            preview(parsed);
            toast.success("Theme imported — review then Save to persist");
        } catch (err) { toast.error(`Import failed: ${err.message}`); }
    };

    if (loading) return <div className="p-10 text-center text-sm text-muted-foreground">Loading branding…</div>;
    if (loadError) return (
        <div className="max-w-2xl mx-auto px-4 py-10" data-testid="branding-no-company">
            <div className="border border-border bg-secondary/40 p-6">
                <h2 className="font-display font-bold text-xl mb-2">Branding unavailable</h2>
                <p className="text-sm text-muted-foreground">
                    Portal branding is configured per company. Your current account isn't tied to a company workspace,
                    so there's nothing to brand here. Sign in as the company owner (employer) to manage logo, colors and fonts.
                </p>
                <p className="text-xs text-muted-foreground/80 mt-3 font-mono">Detail: {loadError}</p>
            </div>
        </div>
    );

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4" data-testid="branding-root">
            {/* Page header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb inline-flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5" /> Workspace · advanced theme studio
                    </div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">Portal branding</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl">
                        Every visual element — colors, fonts, sizes, radiuses, shadows — is controllable here.
                        Changes preview instantly; hit <em>Save</em> to persist and roll out to every recruiter,
                        candidate portal and embed board.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" className="rounded-none" onClick={doExport} data-testid="branding-export">
                        <Download className="w-4 h-4 mr-1.5" /> Export
                    </Button>
                    <Button variant="outline" className="rounded-none" onClick={() => importRef.current?.click()} data-testid="branding-import">
                        <Upload className="w-4 h-4 mr-1.5" /> Import
                    </Button>
                    <input
                        ref={importRef}
                        type="file"
                        accept="application/json"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) doImport(f);
                            e.target.value = "";
                        }}
                        data-testid="branding-import-file"
                    />
                    <Button variant="outline" className="rounded-none" onClick={doReset} data-testid="branding-reset">
                        <RefreshCw className="w-4 h-4 mr-1.5" /> Reset
                    </Button>
                    <Button onClick={save} disabled={saving} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="branding-save">
                        <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-[minmax(0,1fr)_460px] gap-5">
                {/* ─── Form column ─── */}
                <div>
                    {/* Tabs */}
                    <div className="border-b border-border flex items-center gap-1 overflow-x-auto pw-thin-scroll -mx-1 px-1" data-testid="branding-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTab(t.id)}
                                className={`shrink-0 h-9 px-3 text-[11px] font-mono uppercase tracking-widest border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                                    tab === t.id
                                        ? "border-ikb text-ikb"
                                        : "border-transparent text-foreground/70 hover:text-foreground hover:border-border"
                                }`}
                                data-testid={`branding-tab-${t.id}`}
                            >
                                <t.icon className="w-3.5 h-3.5" />
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="pt-5 space-y-5">
                        {tab === "identity" && <IdentityTab theme={theme} preview={preview} />}
                        {tab === "colors" && <ColorsTab theme={theme} preview={preview} />}
                        {tab === "surfaces" && <SurfacesTab theme={theme} preview={preview} />}
                        {tab === "typography" && <TypographyTab theme={theme} preview={preview} />}
                        {tab === "shape" && <ShapeTab theme={theme} preview={preview} />}
                        {tab === "sections" && <SectionsTab theme={theme} preview={preview} schema={sectionSchema} />}
                        {tab === "advanced" && <AdvancedTab theme={theme} preview={preview} />}
                    </div>
                </div>

                {/* ─── Live preview ─── */}
                <div className="lg:sticky lg:top-4 lg:self-start space-y-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-ikb" /> Live preview · reflects unsaved edits
                    </div>
                    <RichPreview theme={theme} />
                </div>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Tabs
// ──────────────────────────────────────────────────────────────────────
function IdentityTab({ theme, preview }) {
    return (
        <Section title="Brand identity" icon={ImageIcon}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Logo URL (light background)">
                    <Input value={theme.logo_url || ""} onChange={(e) => preview({ logo_url: e.target.value })}
                        placeholder="https://…/logo.png" data-testid="branding-logo" />
                    <Hint>Transparent PNG/SVG. Roughly 32px tall renders cleanest in the top nav.</Hint>
                </Field>
                <Field label="Logo URL (dark background, optional)">
                    <Input value={theme.logo_dark_url || ""} onChange={(e) => preview({ logo_dark_url: e.target.value })}
                        placeholder="https://…/logo-white.png" data-testid="branding-logo-dark" />
                    <Hint>Used on dark hero banners and dark-mode navigation.</Hint>
                </Field>
                <Field label="Color scheme">
                    <Select value={theme.color_scheme || "light"} onChange={(v) => preview({ color_scheme: v })}
                        options={[["light", "Light"], ["dark", "Dark"]]} testId="branding-color-scheme" />
                </Field>
                <Field label="Density">
                    <Select value={theme.density || "default"} onChange={(v) => preview({ density: v })}
                        options={[["compact", "Compact"], ["default", "Default"], ["comfortable", "Comfortable"]]} testId="branding-density" />
                </Field>
            </div>
        </Section>
    );
}

function ColorsTab({ theme, preview }) {
    return (
        <>
            <Section title="Core palette" icon={Palette}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <ColorField label="Primary" value={theme.primary_color} onChange={(v) => preview({ primary_color: v })} testId="branding-primary" />
                    <ColorField label="Accent" value={theme.accent_color} onChange={(v) => preview({ accent_color: v })} testId="branding-accent" />
                    <ColorField label="Text" value={theme.text_color} onChange={(v) => preview({ text_color: v })} testId="branding-text-color" />
                    <ColorField label="Muted text" value={theme.muted_color} onChange={(v) => preview({ muted_color: v })} testId="branding-muted-color" />
                    <ColorField label="Border" value={theme.border_color} onChange={(v) => preview({ border_color: v })} testId="branding-border-color" />
                    <ColorField label="Link" value={theme.link_color} onChange={(v) => preview({ link_color: v })} testId="branding-link-color"
                        hint="Blank = same as Primary" />
                </div>
            </Section>
            <Section title="Semantic states" icon={Palette}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <ColorField label="Success" value={theme.success_color} onChange={(v) => preview({ success_color: v })} testId="branding-success-color" />
                    <ColorField label="Warning" value={theme.warning_color} onChange={(v) => preview({ warning_color: v })} testId="branding-warning-color" />
                    <ColorField label="Danger" value={theme.danger_color} onChange={(v) => preview({ danger_color: v })} testId="branding-danger-color" />
                </div>
                <Hint>Used across pipeline badges, budget chips, offer statuses, error banners.</Hint>
            </Section>
        </>
    );
}

function SurfacesTab({ theme, preview }) {
    return (
        <>
            <Section title="Backgrounds" icon={Layers}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <ColorField label="Page background" value={theme.bg_color} onChange={(v) => preview({ bg_color: v })} testId="branding-bg" />
                    <ColorField label="Card background" value={theme.card_bg} onChange={(v) => preview({ card_bg: v })} testId="branding-card-bg" />
                    <ColorField label="Sidebar" value={theme.sidebar_bg} onChange={(v) => preview({ sidebar_bg: v })} testId="branding-sidebar-bg"
                        hint="Blank = same as Card" />
                    <ColorField label="Top header" value={theme.header_bg} onChange={(v) => preview({ header_bg: v })} testId="branding-header-bg"
                        hint="Blank = same as Card" />
                    <ColorField label="Input fields" value={theme.input_bg} onChange={(v) => preview({ input_bg: v })} testId="branding-input-bg" />
                    <ColorField label="Table header" value={theme.table_header_bg} onChange={(v) => preview({ table_header_bg: v })} testId="branding-table-header-bg" />
                    <ColorField label="Tooltips" value={theme.tooltip_bg} onChange={(v) => preview({ tooltip_bg: v })} testId="branding-tooltip-bg" />
                </div>
            </Section>
            <Section title="Interactive tokens" icon={Layers}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <ColorField label="Primary button text" value={theme.button_text_color} onChange={(v) => preview({ button_text_color: v })} testId="branding-btn-text" />
                    <ColorField label="Focus ring" value={theme.focus_ring_color} onChange={(v) => preview({ focus_ring_color: v })} testId="branding-focus-ring"
                        hint="Blank = same as Primary" />
                </div>
            </Section>
        </>
    );
}

function TypographyTab({ theme, preview }) {
    return (
        <>
            <Section title="Font families" icon={Type}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FontField label="Body" value={theme.body_font} onChange={(v) => preview({ body_font: v })} testId="branding-body-font" />
                    <FontField label="Headings (optional)" value={theme.heading_font} onChange={(v) => preview({ heading_font: v })}
                        allowEmpty="Same as body" testId="branding-heading-font" />
                    <FontField label="Display / page titles (optional)" value={theme.display_font} onChange={(v) => preview({ display_font: v })}
                        allowEmpty="Same as headings" testId="branding-display-font" />
                    <FontField label="Mono / code (optional)" value={theme.mono_font} onChange={(v) => preview({ mono_font: v })}
                        allowEmpty="System monospace" testId="branding-mono-font" />
                </div>
                <Hint>Google Fonts loaded automatically for non-system families.</Hint>
            </Section>

            <Section title="Text scale" icon={Type}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <SliderField label="Base size (px)" value={theme.base_font_size ?? 14} min={12} max={18} step={1}
                        onChange={(v) => preview({ base_font_size: v })} testId="branding-base-font-size" />
                    <SliderField label="H1 scale" value={theme.h1_scale ?? 2.25} min={1.2} max={3.5} step={0.05}
                        onChange={(v) => preview({ h1_scale: v })} format={(v) => `${v.toFixed(2)}×`} testId="branding-h1-scale" />
                    <SliderField label="H2 scale" value={theme.h2_scale ?? 1.75} min={1.1} max={2.75} step={0.05}
                        onChange={(v) => preview({ h2_scale: v })} format={(v) => `${v.toFixed(2)}×`} testId="branding-h2-scale" />
                    <SliderField label="H3 scale" value={theme.h3_scale ?? 1.375} min={1.05} max={2.25} step={0.05}
                        onChange={(v) => preview({ h3_scale: v })} format={(v) => `${v.toFixed(2)}×`} testId="branding-h3-scale" />
                    <SliderField label="Line height" value={theme.line_height ?? 1.5} min={1.2} max={2} step={0.05}
                        onChange={(v) => preview({ line_height: v })} format={(v) => v.toFixed(2)} testId="branding-line-height" />
                    <Field label="Letter spacing">
                        <Select value={theme.letter_spacing || "normal"} onChange={(v) => preview({ letter_spacing: v })}
                            options={[["tight", "Tight"], ["normal", "Normal"], ["wide", "Wide"]]} testId="branding-letter-spacing" />
                    </Field>
                </div>
            </Section>

            <Section title="Weights" icon={Type}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <Field label="Body weight">
                        <Select value={String(theme.body_weight ?? 400)} onChange={(v) => preview({ body_weight: Number(v) })}
                            options={[["300", "Light"], ["400", "Regular"], ["500", "Medium"], ["600", "Semi-bold"], ["700", "Bold"]]}
                            testId="branding-body-weight" />
                    </Field>
                    <Field label="Heading weight">
                        <Select value={String(theme.heading_weight ?? 800)} onChange={(v) => preview({ heading_weight: Number(v) })}
                            options={[["500", "Medium"], ["600", "Semi-bold"], ["700", "Bold"], ["800", "Extra-bold"], ["900", "Black"]]}
                            testId="branding-heading-weight" />
                    </Field>
                    <Field label="Page titles">
                        <label className="text-sm inline-flex items-center gap-2 h-10">
                            <input type="checkbox" checked={!!theme.page_title_uppercase}
                                onChange={(e) => preview({ page_title_uppercase: e.target.checked })}
                                data-testid="branding-title-upper" />
                            Uppercase
                        </label>
                    </Field>
                    <Field label="Page title size">
                        <Select value={theme.page_title_size || "default"} onChange={(v) => preview({ page_title_size: v })}
                            options={[["compact", "Compact"], ["default", "Default"], ["xl", "Extra large"]]} testId="branding-title-size" />
                    </Field>
                </div>
            </Section>
        </>
    );
}

function ShapeTab({ theme, preview }) {
    return (
        <>
            <Section title="Corners" icon={Ruler}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <Field label="Button shape">
                        <Select value={theme.button_shape || "sharp"} onChange={(v) => preview({ button_shape: v })}
                            options={[["sharp", "Sharp"], ["soft", "Soft (4px)"], ["rounded", "Rounded (8px)"], ["pill", "Pill"]]} testId="branding-shape" />
                    </Field>
                    <SliderField label="Card radius (px)" value={theme.card_radius ?? 0} min={0} max={24} step={1}
                        onChange={(v) => preview({ card_radius: v })} testId="branding-card-radius" />
                    <SliderField label="Input radius (px)" value={theme.input_radius ?? 0} min={0} max={24} step={1}
                        onChange={(v) => preview({ input_radius: v })} testId="branding-input-radius" />
                </div>
            </Section>
            <Section title="Shadow depth" icon={Ruler}>
                <Field label="Shadow">
                    <Select value={theme.shadow_depth || "none"} onChange={(v) => preview({ shadow_depth: v })}
                        options={[["none", "None (flat)"], ["sm", "Subtle"], ["md", "Medium"], ["lg", "Elevated"]]} testId="branding-shadow" />
                </Field>
                <Hint>Applies to cards and dialogs that opt into <code>.shadow-theme</code>.</Hint>
            </Section>
        </>
    );
}

function SectionsTab({ theme, preview, schema }) {
    // Prefer server-driven definitions; fall back to the hardcoded list when
    // the schema endpoint is unavailable (offline / older backend).
    const sections = schema?.sections?.length ? schema.sections : SECTION_DEFS;
    const allowedKeys = schema?.allowed_keys ? new Set(schema.allowed_keys) : SECTION_ALLOWED_KEYS;
    const fieldMeta = schema?.fields || null;
    const [active, setActive] = useState(sections[0]?.id || "dashboard");
    const overrides = theme.section_overrides || {};
    const patch = overrides[active] || {};

    const setPatch = (updates) => {
        const nextPatch = { ...patch, ...updates };
        // Strip empty values so we don't persist blank overrides.
        for (const [k, v] of Object.entries(nextPatch)) {
            if (v === "" || v === null || v === undefined) delete nextPatch[k];
        }
        const nextAll = { ...overrides };
        if (Object.keys(nextPatch).length === 0) delete nextAll[active];
        else nextAll[active] = nextPatch;
        preview({ section_overrides: nextAll });
    };

    const clearSection = () => {
        const nextAll = { ...overrides };
        delete nextAll[active];
        preview({ section_overrides: nextAll });
    };

    const activeDef = sections.find((s) => s.id === active) || sections[0];
    const configuredCount = Object.keys(patch).filter((k) => allowedKeys.has(k)).length;

    // Group schema-provided fields for auto-rendering when metadata is present.
    const colorFields = fieldMeta
        ? Object.entries(fieldMeta).filter(([, m]) => m.group === "colors").slice(0, 8)
        : null;
    const typoFields = fieldMeta
        ? Object.entries(fieldMeta).filter(([, m]) => m.group === "typography" && ["int", "float", "enum"].includes(m.type)).slice(0, 6)
        : null;
    const shapeFields = fieldMeta
        ? Object.entries(fieldMeta).filter(([, m]) => m.group === "shape").slice(0, 4)
        : null;

    return (
        <>
            <Section title="Per-section overrides" icon={FolderTree}>
                <p className="text-xs text-muted-foreground">
                    Fine-tune the look of individual sections without touching global brand colors. For example,
                    reduce base font size inside <em>Candidate profile</em> for a denser view, or override
                    <em> Public apply</em> to match your corporate marketing site palette.
                </p>
                <div className="flex flex-wrap gap-1 pt-2" data-testid="sections-list">
                    {sections.map((s) => {
                        const isSet = !!overrides[s.id] && Object.keys(overrides[s.id]).length > 0;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => setActive(s.id)}
                                className={`h-8 px-3 border text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${
                                    active === s.id ? "border-ikb bg-ikb/5 text-ikb" : "border-border hover:border-foreground"
                                }`}
                                data-testid={`section-tab-${s.id}`}
                            >
                                {s.label}
                                {isSet && <span className="w-1.5 h-1.5 bg-ikb ml-1" title="Has overrides" />}
                            </button>
                        );
                    })}
                </div>
                <div className="text-[11px] text-muted-foreground border-l-2 border-border pl-2 mt-3">
                    {activeDef?.hint}
                    {configuredCount > 0 && (
                        <>
                            {" "}
                            <button
                                type="button"
                                onClick={clearSection}
                                className="inline-flex items-center gap-1 text-rose-700 hover:underline font-mono text-[10px] uppercase tracking-widest ml-2"
                                data-testid={`section-clear-${active}`}
                            >
                                <RotateCcw className="w-3 h-3" /> Clear {configuredCount} override{configuredCount > 1 ? "s" : ""}
                            </button>
                        </>
                    )}
                </div>
            </Section>

            <Section title={`Colors for "${activeDef?.label}"`} icon={Palette}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <SectionColorField label="Primary" k="primary_color" theme={theme} patch={patch} setPatch={setPatch} testId="section-primary" />
                    <SectionColorField label="Page bg" k="bg_color" theme={theme} patch={patch} setPatch={setPatch} testId="section-bg" />
                    <SectionColorField label="Card bg" k="card_bg" theme={theme} patch={patch} setPatch={setPatch} testId="section-card-bg" />
                    <SectionColorField label="Text" k="text_color" theme={theme} patch={patch} setPatch={setPatch} testId="section-text" />
                    <SectionColorField label="Muted text" k="muted_color" theme={theme} patch={patch} setPatch={setPatch} testId="section-muted" />
                    <SectionColorField label="Border" k="border_color" theme={theme} patch={patch} setPatch={setPatch} testId="section-border" />
                    <SectionColorField label="Link" k="link_color" theme={theme} patch={patch} setPatch={setPatch} testId="section-link" />
                    <SectionColorField label="Accent" k="accent_color" theme={theme} patch={patch} setPatch={setPatch} testId="section-accent" />
                </div>
            </Section>

            <Section title="Typography density" icon={Type}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <SectionSlider label="Base size (px)" k="base_font_size" min={11} max={18} step={1}
                        theme={theme} patch={patch} setPatch={setPatch} testId="section-base-size" />
                    <SectionSlider label="Line height" k="line_height" min={1.2} max={2} step={0.05}
                        theme={theme} patch={patch} setPatch={setPatch} format={(v) => v.toFixed(2)} testId="section-line-height" />
                    <SectionSlider label="H1 scale" k="h1_scale" min={1.2} max={3.5} step={0.05}
                        theme={theme} patch={patch} setPatch={setPatch} format={(v) => `${v.toFixed(2)}×`} testId="section-h1" />
                    <SectionSlider label="H2 scale" k="h2_scale" min={1.1} max={2.75} step={0.05}
                        theme={theme} patch={patch} setPatch={setPatch} format={(v) => `${v.toFixed(2)}×`} testId="section-h2" />
                    <Field label="Letter spacing">
                        <select
                            className="w-full h-10 px-2 border border-border bg-white text-sm"
                            value={patch.letter_spacing ?? ""}
                            onChange={(e) => setPatch({ letter_spacing: e.target.value || null })}
                            data-testid="section-letter-spacing"
                        >
                            <option value="">(inherit)</option>
                            <option value="tight">Tight</option>
                            <option value="normal">Normal</option>
                            <option value="wide">Wide</option>
                        </select>
                    </Field>
                </div>
                <Hint>Tuning base font down 1–2px in <em>Candidate profile</em> makes the dense multi-section
                view feel airier without changing the rest of the portal.</Hint>
            </Section>

            <Section title="Shape overrides" icon={Ruler}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <SectionSlider label="Card radius" k="card_radius" min={0} max={24} step={1}
                        theme={theme} patch={patch} setPatch={setPatch} testId="section-card-radius" />
                    <SectionSlider label="Input radius" k="input_radius" min={0} max={24} step={1}
                        theme={theme} patch={patch} setPatch={setPatch} testId="section-input-radius" />
                    <Field label="Button shape">
                        <select
                            className="w-full h-10 px-2 border border-border bg-white text-sm"
                            value={patch.button_shape ?? ""}
                            onChange={(e) => setPatch({ button_shape: e.target.value || null })}
                            data-testid="section-button-shape"
                        >
                            <option value="">(inherit)</option>
                            <option value="sharp">Sharp</option>
                            <option value="soft">Soft</option>
                            <option value="rounded">Rounded</option>
                            <option value="pill">Pill</option>
                        </select>
                    </Field>
                </div>
            </Section>
        </>
    );
}

function SectionColorField({ label, k, theme, patch, setPatch, testId }) {
    const value = patch[k] ?? "";
    const inherited = theme[k] || "";
    return (
        <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block flex items-center justify-between">
                <span>{label}</span>
                {value && (
                    <button type="button" onClick={() => setPatch({ [k]: null })}
                        className="text-[9px] text-rose-700 hover:underline" data-testid={`${testId}-clear`}>reset</button>
                )}
            </Label>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value || inherited || "#000000"}
                    onChange={(e) => setPatch({ [k]: e.target.value })}
                    className={`w-9 h-9 border cursor-pointer p-0 bg-white ${value ? "border-ikb" : "border-border opacity-60"}`}
                    data-testid={testId}
                />
                <Input
                    value={value}
                    onChange={(e) => setPatch({ [k]: e.target.value })}
                    placeholder={inherited || "inherit"}
                    className="rounded-none h-9 flex-1 font-mono text-xs"
                />
            </div>
        </div>
    );
}

function SectionSlider({ label, k, min, max, step, theme, patch, setPatch, format, testId }) {
    const isSet = patch[k] !== undefined && patch[k] !== null && patch[k] !== "";
    const current = isSet ? patch[k] : (theme[k] ?? min);
    const display = format ? format(current) : String(current);
    return (
        <Field label={label}>
            <div className="flex items-center gap-2">
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={current}
                    onChange={(e) => setPatch({ [k]: Number(e.target.value) })}
                    className={`flex-1 accent-ikb ${!isSet ? "opacity-60" : ""}`}
                    data-testid={testId}
                />
                <span className={`text-[11px] font-mono w-14 text-right shrink-0 ${isSet ? "text-ikb" : "text-muted-foreground"}`}>{display}</span>
                {isSet && (
                    <button type="button" onClick={() => setPatch({ [k]: null })}
                        className="text-[9px] text-rose-700 hover:underline font-mono uppercase" data-testid={`${testId}-clear`}>reset</button>
                )}
            </div>
        </Field>
    );
}

function AdvancedTab({ theme, preview }) {
    return (
        <Section title="Custom CSS (advanced escape hatch)" icon={Wand2}>
            <Field label="Additional CSS">
                <Textarea
                    value={theme.custom_css || ""}
                    onChange={(e) => preview({ custom_css: e.target.value })}
                    rows={12}
                    placeholder={`/* Example: override the sidebar width\n:root { --sidebar-width: 260px; }\n.pw-sidebar { background: linear-gradient(180deg, #0F172A, #002FA7); }\n*/`}
                    className="font-mono text-xs"
                    data-testid="branding-custom-css"
                />
            </Field>
            <Hint>
                Injected into a single <code>&lt;style&gt;</code> tag near the end of <code>&lt;head&gt;</code>.
                Blocked tokens: <code>&lt;script&gt;</code>, <code>javascript:</code>, <code>expression()</code>,
                <code>-moz-binding</code>, <code>behavior:</code>. Save-time server-side validation will reject
                any string containing these.
            </Hint>
        </Section>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Rich preview — shows every visual element responding to the theme
// ──────────────────────────────────────────────────────────────────────
function RichPreview({ theme }) {
    const style = useMemo(() => ({
        background: theme.bg_color || "#F9FAFB",
        color: theme.text_color || "#0f172a",
        border: `1px solid ${theme.border_color || "#e2e8f0"}`,
        fontFamily: theme.body_font || "Inter, system-ui, sans-serif",
        fontSize: `${theme.base_font_size || 14}px`,
        lineHeight: theme.line_height || 1.5,
    }), [theme]);

    const btnRadius = ({ sharp: 0, soft: 4, rounded: 8, pill: 999 }[theme.button_shape || "sharp"]);
    const cardRadius = theme.card_radius ?? 0;
    const inputRadius = theme.input_radius ?? 0;
    const shadows = { none: "none", sm: "0 1px 2px rgba(15,23,42,0.06)", md: "0 4px 12px rgba(15,23,42,0.10)", lg: "0 12px 32px rgba(15,23,42,0.14)" };
    const shadow = shadows[theme.shadow_depth || "none"];
    const headingFont = theme.display_font || theme.heading_font || theme.body_font;
    const letterSpacing = { tight: "-0.01em", normal: "0", wide: "0.02em" }[theme.letter_spacing || "normal"];

    return (
        <div style={style} data-testid="branding-preview" className="p-4 space-y-3">
            {/* Header row */}
            <div style={{
                background: theme.header_bg || theme.card_bg || "#fff",
                border: `1px solid ${theme.border_color || "#e2e8f0"}`,
                padding: "8px 12px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                borderRadius: cardRadius,
                boxShadow: shadow,
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {theme.logo_url
                        ? <img src={theme.logo_url} alt="logo" style={{ height: 24 }} />
                        : <div style={{ width: 24, height: 24, background: theme.primary_color, display: "grid", placeItems: "center", color: "#fff", fontWeight: 800 }}>A</div>}
                    <span style={{ fontFamily: headingFont, fontWeight: theme.heading_weight || 800, fontSize: 14, letterSpacing }}>Acme Talent</span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    <span style={{ fontSize: 11, color: theme.muted_color || "#64748B" }}>128 candidates</span>
                </div>
            </div>

            {/* Sidebar + main */}
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 8 }}>
                <div style={{
                    background: theme.sidebar_bg || theme.card_bg || "#fff",
                    border: `1px solid ${theme.border_color || "#e2e8f0"}`,
                    padding: "10px 8px",
                    borderRadius: cardRadius,
                    fontSize: 11,
                    letterSpacing,
                }}>
                    <div style={{ color: theme.primary_color, fontWeight: 700, marginBottom: 8 }}>Dashboard</div>
                    <div style={{ color: theme.muted_color || "#64748B" }}>Jobs</div>
                    <div style={{ color: theme.muted_color || "#64748B", marginTop: 4 }}>Candidates</div>
                    <div style={{ color: theme.muted_color || "#64748B", marginTop: 4 }}>Pipeline</div>
                </div>

                <div style={{
                    background: theme.card_bg || "#fff",
                    border: `1px solid ${theme.border_color || "#e2e8f0"}`,
                    padding: 12,
                    borderRadius: cardRadius,
                    boxShadow: shadow,
                }}>
                    <div style={{
                        fontFamily: headingFont,
                        fontWeight: theme.heading_weight || 800,
                        fontSize: `${(theme.h1_scale || 2.25) * (theme.base_font_size || 14)}px`,
                        letterSpacing,
                        textTransform: theme.page_title_uppercase ? "uppercase" : "none",
                    }}>
                        Senior Backend Engineer
                    </div>
                    <div style={{ color: theme.muted_color || "#64748B", fontSize: 12, marginTop: 2 }}>
                        Remote · Full-time · 3-7 yrs
                    </div>
                    {/* Input + button */}
                    <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                        <input
                            defaultValue="Search"
                            style={{
                                flex: 1,
                                background: theme.input_bg || "#fff",
                                border: `1px solid ${theme.border_color || "#e2e8f0"}`,
                                padding: "6px 8px",
                                fontSize: 12,
                                borderRadius: inputRadius,
                                fontFamily: theme.body_font,
                                color: theme.text_color,
                            }}
                        />
                        <button
                            style={{
                                background: theme.primary_color || "#002FA7",
                                color: theme.button_text_color || "#fff",
                                border: "none",
                                padding: "6px 12px",
                                fontWeight: 600,
                                fontSize: 12,
                                cursor: "pointer",
                                borderRadius: btnRadius,
                                fontFamily: theme.body_font,
                            }}
                        >Apply</button>
                    </div>
                    {/* Table */}
                    <table style={{ width: "100%", marginTop: 10, borderCollapse: "collapse", fontSize: 11 }}>
                        <thead>
                            <tr style={{ background: theme.table_header_bg || "#F1F5F9" }}>
                                <th style={{ padding: 6, textAlign: "left", fontWeight: 600 }}>Candidate</th>
                                <th style={{ padding: 6, textAlign: "left", fontWeight: 600 }}>Stage</th>
                                <th style={{ padding: 6, textAlign: "right", fontWeight: 600 }}>Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderTop: `1px solid ${theme.border_color || "#e2e8f0"}` }}>
                                <td style={{ padding: 6 }}>Priya Shah</td>
                                <td style={{ padding: 6 }}>
                                    <Chip color={theme.success_color || "#059669"} label="Hired" />
                                </td>
                                <td style={{ padding: 6, textAlign: "right" }}>92</td>
                            </tr>
                            <tr style={{ borderTop: `1px solid ${theme.border_color || "#e2e8f0"}` }}>
                                <td style={{ padding: 6 }}>Alex Rivera</td>
                                <td style={{ padding: 6 }}>
                                    <Chip color={theme.warning_color || "#D97706"} label="Interview" />
                                </td>
                                <td style={{ padding: 6, textAlign: "right" }}>78</td>
                            </tr>
                            <tr style={{ borderTop: `1px solid ${theme.border_color || "#e2e8f0"}` }}>
                                <td style={{ padding: 6 }}>Jordan Lee</td>
                                <td style={{ padding: 6 }}>
                                    <Chip color={theme.danger_color || "#DC2626"} label="Rejected" />
                                </td>
                                <td style={{ padding: 6, textAlign: "right" }}>34</td>
                            </tr>
                        </tbody>
                    </table>
                    {/* Link + accent */}
                    <div style={{ marginTop: 10, fontSize: 12 }}>
                        <a href="#" onClick={(e) => e.preventDefault()} style={{ color: theme.link_color || theme.primary_color, textDecoration: "underline" }}>
                            View full report →
                        </a>
                        <span style={{ marginLeft: 12, color: theme.accent_color || "#0080FF" }}>Accent</span>
                    </div>
                </div>
            </div>

            {/* Tooltip preview */}
            <div style={{
                background: theme.tooltip_bg || "#0f172a",
                color: "#fff",
                padding: "4px 8px",
                fontSize: 11,
                display: "inline-block",
                borderRadius: inputRadius,
                fontFamily: theme.body_font,
            }}>
                Tooltip · uses tooltip_bg
            </div>
        </div>
    );
}

function Chip({ color, label }) {
    return (
        <span style={{
            display: "inline-block",
            fontSize: 10,
            fontWeight: 600,
            padding: "1px 6px",
            color,
            border: `1px solid ${color}`,
            background: `${color}14`, // 8% alpha
        }}>{label}</span>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Small primitives
// ──────────────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
    return (
        <section className="border border-border bg-white p-4 sm:p-5 space-y-3">
            <div className="inline-flex items-center gap-2 font-display font-bold">
                {Icon && <Icon className="w-4 h-4 text-ikb" />}
                {title}
            </div>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">{label}</Label>
            {children}
        </div>
    );
}

function Hint({ children }) {
    return <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{children}</p>;
}

function ColorField({ label, value, onChange, testId, hint }) {
    return (
        <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 block">{label}</Label>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value || "#000000"}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-9 h-9 border border-border cursor-pointer p-0 bg-white"
                    data-testid={testId}
                />
                <Input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="rounded-none h-9 flex-1 font-mono text-xs"
                    placeholder="#000000"
                />
            </div>
            {hint && <Hint>{hint}</Hint>}
        </div>
    );
}

function FontField({ label, value, onChange, allowEmpty, testId }) {
    return (
        <Field label={label}>
            <select
                className="w-full h-10 px-2 border border-border bg-white text-sm"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
            >
                {allowEmpty && <option value="">{allowEmpty}</option>}
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f.split(",")[0]}</option>)}
            </select>
        </Field>
    );
}

function Select({ value, onChange, options, testId }) {
    return (
        <select
            className="w-full h-10 px-2 border border-border bg-white text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
        >
            {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
    );
}

function SliderField({ label, value, min, max, step, onChange, format, testId }) {
    const display = format ? format(value) : String(value);
    return (
        <Field label={label}>
            <div className="flex items-center gap-2">
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="flex-1 accent-ikb"
                    data-testid={testId}
                />
                <span className="text-[11px] font-mono w-14 text-right shrink-0">{display}</span>
            </div>
        </Field>
    );
}
